import React, { useState, useEffect, useCallback } from 'react';

interface GrammarQuestion {
  question: string;
  options: string[];
  correct: number;
  explanation: string;
  level: number;
}

interface CurrentQuestion extends GrammarQuestion {
  id: number;
}

const GrammarDefender: React.FC = () => {
  const [score, setScore] = useState<number>(0);
  const [cometDistance, setCometDistance] = useState<number>(100); // 100 = far away, 0 = impact
  const [level, setLevel] = useState<number>(1);
  const [gameState, setGameState] = useState<'menu' | 'playing' | 'waiting' | 'feedback' | 'exploding' | 'gameOver'>('menu');
  const [currentQuestion, setCurrentQuestion] = useState<CurrentQuestion | null>(null);
  const [timeLeft, setTimeLeft] = useState<number>(20);
  const [powerUps, setPowerUps] = useState<{ forceField: boolean; gravityBoost: boolean }>({ forceField: false, gravityBoost: false });
  const [streak, setStreak] = useState<number>(0);
  const [feedback, setFeedback] = useState<string>('');
  const [feedbackType, setFeedbackType] = useState<'correct' | 'incorrect' | 'timeout' | 'levelup' | ''>('');
  const [questionsAnswered, setQuestionsAnswered] = useState<number>(0);
  const [threatLevel, setThreatLevel] = useState<'safe' | 'warning' | 'danger' | 'critical'>('safe');

  // Grammar questions database
  const grammarQuestions: GrammarQuestion[] = [
    // Level 1 - Basic punctuation
    {
      question: "Which sentence uses the comma correctly?",
      options: [
        "I went to the store and bought milk.",
        "I went to the store, and I bought milk.",
        "I went, to the store and bought milk."
      ],
      correct: 1,
      explanation: "Use a comma before 'and' when joining two complete sentences (independent clauses).",
      level: 1
    },
    {
      question: "Choose the sentence with correct punctuation:",
      options: [
        "Its a beautiful day outside!",
        "It's a beautiful day outside!",
        "Its' a beautiful day outside!"
      ],
      correct: 1,
      explanation: "It's = It is. Use an apostrophe for contractions! 'Its' (no apostrophe) shows possession.",
      level: 1
    },
    {
      question: "Which sentence is punctuated correctly?",
      options: [
        "Wow what a great game",
        "Wow, what a great game!",
        "Wow what a great game."
      ],
      correct: 1,
      explanation: "Exclamations need exclamation marks, and 'wow' needs a comma after it!",
      level: 1
    },
    {
      question: "Pick the sentence with correct apostrophe use:",
      options: [
        "The dogs' toys are everywhere.",
        "The dog's toys are everywhere.",
        "The dogs toy's are everywhere."
      ],
      correct: 1,
      explanation: "For one dog owning toys, use dog's. The apostrophe shows the dog owns the toys!",
      level: 1
    },
    // Level 2 - Parts of speech
    {
      question: "Which word is the VERB in this sentence: 'The quick brown fox jumps over the fence'?",
      options: [
        "quick",
        "jumps",
        "fence"
      ],
      correct: 1,
      explanation: "A verb shows action or being. 'Jumps' is the action the fox is doing!",
      level: 2
    },
    {
      question: "Find the ADJECTIVE: 'She runs quickly to the tall building'",
      options: [
        "quickly",
        "runs",
        "tall"
      ],
      correct: 2,
      explanation: "Adjectives describe nouns. 'Tall' describes the building. 'Quickly' is an adverb!",
      level: 2
    },
    {
      question: "Which word is the ADVERB: 'The cat quietly crept through the dark room'?",
      options: [
        "quietly",
        "dark",
        "crept"
      ],
      correct: 0,
      explanation: "Adverbs describe verbs and often end in -ly. 'Quietly' describes HOW the cat crept!",
      level: 2
    },
    // Level 3 - Advanced grammar
    {
      question: "Which sentence fixes the dangling modifier?",
      options: [
        "Walking to school, the backpack felt heavy.",
        "Walking to school, I felt the backpack was heavy.",
        "The backpack was heavy walking to school."
      ],
      correct: 1,
      explanation: "The person walks, not the backpack! Make sure the subject doing the action is clear.",
      level: 3
    },
    {
      question: "Choose the sentence with parallel structure:",
      options: [
        "I like reading, writing, and to draw.",
        "I like reading, writing, and drawing.",
        "I like to read, writing, and drawing."
      ],
      correct: 1,
      explanation: "Keep items in a list the same format: all -ing words or all 'to' words!",
      level: 3
    }
  ];

  // Update threat level based on comet distance
  useEffect(() => {
    if (cometDistance > 75) setThreatLevel('safe');
    else if (cometDistance > 50) setThreatLevel('warning');
    else if (cometDistance > 25) setThreatLevel('danger');
    else setThreatLevel('critical');
  }, [cometDistance]);

  // Get appropriate questions for current level
  const getQuestionsForLevel = (currentLevel: number): GrammarQuestion[] => {
    return grammarQuestions.filter(q => q.level === currentLevel);
  };

  // Initialize new question
  const presentNewQuestion = () => {
    const levelQuestions = getQuestionsForLevel(level);
    const randomQuestion = levelQuestions[Math.floor(Math.random() * levelQuestions.length)];
    
    const newQuestion: CurrentQuestion = {
      ...randomQuestion,
      id: Date.now()
    };
    
    setCurrentQuestion(newQuestion);
    
    // Set time based on level and power-ups
    const baseTime = level === 1 ? 25 : level === 2 ? 20 : 18;
    const bonusTime = powerUps.gravityBoost ? 10 : 0;
    setTimeLeft(baseTime + bonusTime);
    setGameState('playing');
  };

  // Timer countdown
  useEffect(() => {
    if (gameState !== 'playing' || !currentQuestion) return;

    const timer = setInterval(() => {
      setTimeLeft(prev => {
        if (prev <= 1) {
          // Time ran out!
          handleTimeout();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [gameState, currentQuestion]);

  // Handle timeout (time ran out)
  const handleTimeout = () => {
    if (!powerUps.forceField) {
      setCometDistance(prev => {
        const newDistance = Math.max(0, prev - 15);
        if (newDistance <= 0) {
          // Trigger explosion sequence and auto-restart
          setGameState('exploding');
          // Show explosion for 4 seconds, then auto-restart
          setTimeout(() => {
            initializeGame(); // Auto-restart instead of game over
          }, 4000);
          return 0;
        }
        return newDistance;
      });
    }
    
    setStreak(0);
    setFeedback(currentQuestion ? `⏰ Time's up! ${currentQuestion.explanation}` : "Time's up!");
    setFeedbackType('timeout');
    setGameState('feedback');
    
    // Show feedback then proceed
    setTimeout(() => {
      setCurrentQuestion(null);
      proceedToNext();
    }, 4000);
  };

  // Handle answer selection
  const selectAnswer = (selectedOption: number): void => {
    if (!currentQuestion || gameState !== 'playing') return;

    const isCorrect = selectedOption === currentQuestion.correct;
    
    if (isCorrect) {
      // Correct answer - comet held at bay or pushed back
      setScore(prev => prev + (100 * level) + (timeLeft * 5));
      setStreak(prev => {
        const newStreak = prev + 1;
        
        // Power-up rewards
        if (newStreak === 3 && !powerUps.forceField) {
          setPowerUps(prev => ({ ...prev, forceField: true }));
          setTimeout(() => {
            setPowerUps(prev => ({ ...prev, forceField: false }));
          }, 15000); // 15 second force field
        } else if (newStreak === 5) {
          setPowerUps(prev => ({ ...prev, gravityBoost: true }));
          // Push comet back slightly on gravity boost activation
          setCometDistance(prev => Math.min(100, prev + 10));
          setTimeout(() => {
            setPowerUps(prev => ({ ...prev, gravityBoost: false }));
          }, 10000); // 10 second gravity boost
        }
        
        return newStreak;
      });
      
      // Small comet pushback for correct answers
      setCometDistance(prev => Math.min(100, prev + 2));
      
      setFeedback(`🛡️ GRAMMAR DEFLECTOR ACTIVATED! ${currentQuestion.explanation}`);
      setFeedbackType('correct');
    } else {
      // Wrong answer - comet advances
      setStreak(0);
      
      if (!powerUps.forceField) {
        setCometDistance(prev => {
          const newDistance = Math.max(0, prev - 12);
          if (newDistance <= 0) {
            // Trigger explosion sequence instead of immediate game over
            setGameState('exploding');
            // Show explosion for 3 seconds, then game over
            setTimeout(() => {
              setGameState('gameOver');
            }, 3000);
            return 0;
          }
          return newDistance;
        });
      }
      
      setFeedback(`☄️ GRAMMAR ERROR! Comet advances! ${currentQuestion.explanation}`);
      setFeedbackType('incorrect');
    }

    setGameState('feedback');
    setQuestionsAnswered(prev => prev + 1);
    
    // Show feedback, then proceed
    setTimeout(() => {
      setCurrentQuestion(null);
      proceedToNext();
    }, 4000);
  };

  // Proceed to next question or level
  const proceedToNext = () => {
    setFeedback('');
    setFeedbackType('');
    
    // Level up check (every 5 questions)
    if (questionsAnswered > 0 && questionsAnswered % 5 === 0 && level < 3) {
      setLevel(prev => prev + 1);
      setGameState('waiting');
      setFeedback(`🚀 LEVEL UP! More complex grammar threats detected! Level ${level + 1} incoming!`);
      setFeedbackType('levelup');
      
      setTimeout(() => {
        setFeedback('');
        presentNewQuestion();
      }, 3000);
    } else {
      // Brief pause before next question
      setGameState('waiting');
      setTimeout(() => {
        presentNewQuestion();
      }, 1500);
    }
  };

  // Game initialization
  const initializeGame = () => {
    setScore(0);
    setCometDistance(100);
    setLevel(1);
    setStreak(0);
    setQuestionsAnswered(0);
    setCurrentQuestion(null);
    setPowerUps({ forceField: false, gravityBoost: false });
    setFeedback('');
    setFeedbackType('');
    setGameState('waiting');
    
    // Brief intro before first question
    setFeedback('🚨 MASSIVE GRAMMAR COMET DETECTED! 🚨');
    setTimeout(() => {
      setFeedback('Deploying grammar deflector shields...');
      setTimeout(() => {
        setFeedback('');
        presentNewQuestion();
      }, 1500);
    }, 2000);
  };

  // Get comet visual properties based on distance
  const getCometProps = () => {
    const size = Math.max(20, 120 - cometDistance);
    const opacity = Math.max(0.3, 1 - (cometDistance / 150));
    const glow = cometDistance < 50 ? 'drop-shadow-2xl' : cometDistance < 75 ? 'drop-shadow-lg' : '';
    
    return { size, opacity, glow };
  };

  // Get background color based on threat level
  const getBackgroundGradient = () => {
    switch (threatLevel) {
      case 'critical': return 'from-red-900 via-orange-900 to-black';
      case 'danger': return 'from-orange-900 via-red-800 to-black';
      case 'warning': return 'from-yellow-800 via-orange-800 to-black';
      default: return 'from-blue-900 via-purple-900 to-black';
    }
  };

  // Menu screen
  if (gameState === 'menu') {
    return (
      <div className="min-h-screen bg-gradient-to-b from-purple-900 via-blue-900 to-black text-white flex flex-col items-center justify-center p-4">
        <div className="text-center max-w-2xl">
          <h1 className="text-6xl font-bold mb-4 text-yellow-400 animate-pulse">🛡️ GRAMMAR DEFENDER 🛡️</h1>
          <p className="text-xl mb-8 text-blue-200">
            A massive grammar comet is heading toward Earth! Use your grammar knowledge to power the deflector shields and save our planet!
          </p>
          <div className="mb-8 text-lg text-green-300 space-y-2">
            <p>✅ <strong>Correct answers</strong> hold the comet at bay</p>
            <p>❌ <strong>Wrong answers</strong> let the comet get closer</p>
            <p>🛡️ <strong>3-streak:</strong> Force Field protects from mistakes</p>
            <p>🌌 <strong>5-streak:</strong> Gravity Boost pushes comet back</p>
            <p>📈 <strong>Level up every 5 questions</strong> for harder grammar</p>
          </div>
          <button 
            onClick={initializeGame}
            className="bg-green-500 hover:bg-green-600 text-white font-bold py-4 px-8 rounded-lg text-2xl transform hover:scale-105 transition-all shadow-lg"
          >
            🚀 ACTIVATE DEFENSES
          </button>
        </div>
      </div>
    );
  }

  // Explosion screen
  if (gameState === 'exploding') {
    return (
      <div className="min-h-screen bg-black text-white relative overflow-hidden">
        {/* Intense explosion background */}
        <div className="absolute inset-0 bg-gradient-to-r from-red-600 via-orange-500 to-yellow-400 animate-pulse">
          {/* Multiple explosion rings */}
          {[...Array(8)].map((_, i) => (
            <div
              key={i}
              className="absolute rounded-full border-8 border-white animate-ping"
              style={{
                left: '60%', // Near where Earth was
                top: '70%',
                width: `${(i + 1) * 150}px`,
                height: `${(i + 1) * 150}px`,
                marginLeft: `-${(i + 1) * 75}px`,
                marginTop: `-${(i + 1) * 75}px`,
                animationDelay: `${i * 0.1}s`,
                animationDuration: '1.5s',
                opacity: 0.8 - (i * 0.08)
              }}
            />
          ))}
          
          {/* Debris explosion particles */}
          {[...Array(30)].map((_, i) => (
            <div
              key={i}
              className="absolute animate-bounce"
              style={{
                left: `${55 + Math.random() * 15}%`,
                top: `${60 + Math.random() * 20}%`,
                animationDelay: `${Math.random() * 1}s`,
                animationDuration: `${0.8 + Math.random() * 1.2}s`,
                fontSize: `${20 + Math.random() * 20}px`
              }}
            >
              {['💥', '🔥', '💨', '⚡', '✨'][Math.floor(Math.random() * 5)]}
            </div>
          ))}
        </div>

        {/* Comet and Earth impact scene - positioned where the Earth actually is (further bottom right) */}
        <div className="absolute -bottom-32 -right-40">
          <div className="relative">
            {/* Massive exploding Earth - matching the repositioned size */}
            <div className="animate-spin relative z-10" 
            style={{ 
              fontSize: '18rem',
              animationDuration: '0.2s' 
            }}>
              💥
            </div>
            
            {/* Earth fragments scattered around the explosion */}
            <div className="absolute -top-20 -left-20 animate-bounce" style={{ fontSize: '9rem' }}>🌍</div>
            <div className="absolute -top-28 -right-12 text-7xl animate-bounce" style={{ animationDelay: '0.1s' }}>🪨</div>
            <div className="absolute -bottom-28 -left-12 text-7xl animate-bounce" style={{ animationDelay: '0.3s' }}>🪨</div>
            <div className="absolute -bottom-20 -right-20 text-6xl animate-bounce" style={{ animationDelay: '0.5s' }}>🪨</div>
            <div className="absolute top-28 -left-28 text-6xl animate-bounce" style={{ animationDelay: '0.2s' }}>🪨</div>
            <div className="absolute top-28 right-28 text-5xl animate-bounce" style={{ animationDelay: '0.4s' }}>🪨</div>
            
            {/* Comet embedded in the explosion */}
            <div className="absolute top-10 left-10 animate-pulse z-20" style={{ fontSize: '10rem' }}>
              ☄️
            </div>
          </div>
          
          {/* Fire effects around the explosion */}
          <div className="absolute -top-36 -left-36 animate-spin" style={{ fontSize: '9rem' }}>🔥</div>
          <div className="absolute -top-36 -right-36 animate-spin" style={{ fontSize: '9rem', animationDirection: 'reverse' }}>🔥</div>
          <div className="absolute -bottom-36 -left-36 animate-spin" style={{ fontSize: '9rem', animationDelay: '0.3s' }}>🔥</div>
          <div className="absolute -bottom-36 -right-36 animate-spin" style={{ fontSize: '9rem', animationDirection: 'reverse', animationDelay: '0.3s' }}>🔥</div>
          
          {/* Explosion bursts around the impact */}
          <div className="absolute -top-10 left-20 text-7xl animate-ping">💥</div>
          <div className="absolute top-20 -right-10 text-7xl animate-ping" style={{ animationDelay: '0.2s' }}>💥</div>
          <div className="absolute -bottom-10 -left-10 text-6xl animate-ping" style={{ animationDelay: '0.4s' }}>💥</div>
          <div className="absolute bottom-20 right-20 text-6xl animate-ping" style={{ animationDelay: '0.6s' }}>💥</div>
        </div>

        {/* Impact text overlay */}
        <div className="absolute inset-0 flex items-center justify-center z-30">
          <div className="text-center">
            <h1 className="text-8xl font-bold text-white animate-pulse mb-4" style={{ textShadow: '4px 4px 8px rgba(0,0,0,0.8)' }}>
              💥 IMPACT! 💥
            </h1>
            <p className="text-3xl text-yellow-200 animate-bounce mb-4">
              The Grammar Comet has struck Earth!
            </p>
            <p className="text-2xl text-blue-200 animate-pulse">
              Rebuilding defenses...
            </p>
          </div>
        </div>
      </div>
    );
  }
  if (gameState === 'gameOver') {
    return (
      <div className="min-h-screen bg-gradient-to-b from-red-900 via-orange-900 to-black text-white relative overflow-hidden">
        {/* Impact animation background */}
        <div className="absolute inset-0">
          {/* Explosion rings */}
          {[...Array(5)].map((_, i) => (
            <div
              key={i}
              className="absolute rounded-full border-4 border-yellow-400 animate-ping"
              style={{
                left: '50%',
                top: '50%',
                width: `${(i + 1) * 200}px`,
                height: `${(i + 1) * 200}px`,
                marginLeft: `-${(i + 1) * 100}px`,
                marginTop: `-${(i + 1) * 100}px`,
                animationDelay: `${i * 0.2}s`,
                animationDuration: '2s',
                opacity: 0.7 - (i * 0.1)
              }}
            />
          ))}
          
          {/* Debris particles */}
          {[...Array(20)].map((_, i) => (
            <div
              key={i}
              className="absolute w-2 h-2 bg-orange-400 rounded-full animate-bounce"
              style={{
                left: `${45 + Math.random() * 10}%`,
                top: `${45 + Math.random() * 10}%`,
                animationDelay: `${Math.random() * 2}s`,
                animationDuration: `${1 + Math.random() * 2}s`
              }}
            />
          ))}
        </div>

        {/* Central impact scene */}
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="relative">
            {/* Comet crashed into Earth */}
            <div className="relative">
              {/* Earth being destroyed */}
              <div className="text-8xl animate-pulse relative z-10">🌍</div>
              
              {/* Comet embedded in Earth */}
              <div className="absolute top-4 left-4 text-6xl animate-bounce z-20">
                ☄️
              </div>
              
              {/* Massive explosion at impact point */}
              <div className="absolute top-2 left-2 text-8xl animate-ping z-15">💥</div>
            </div>
            
            {/* Fire effects around impact */}
            <div className="absolute -top-8 -left-8 text-6xl animate-spin">🔥</div>
            <div className="absolute -top-8 -right-8 text-6xl animate-spin" style={{animationDirection: 'reverse'}}>🔥</div>
            <div className="absolute -bottom-8 -left-8 text-6xl animate-spin" style={{animationDelay: '0.5s'}}>🔥</div>
            <div className="absolute -bottom-8 -right-8 text-6xl animate-spin" style={{animationDirection: 'reverse', animationDelay: '0.5s'}}>🔥</div>
            
            {/* Additional explosion effects */}
            <div className="absolute top-0 right-0 text-4xl animate-bounce" style={{animationDelay: '0.3s'}}>💥</div>
            <div className="absolute bottom-0 left-0 text-4xl animate-bounce" style={{animationDelay: '0.7s'}}>💥</div>
          </div>
        </div>

        {/* Game over content overlay */}
        <div className="relative z-20 min-h-screen flex flex-col items-center justify-center p-4">
          <div className="text-center max-w-2xl bg-black bg-opacity-80 p-8 rounded-xl">
            <h1 className="text-6xl font-bold mb-4 text-red-400 animate-pulse">💥 COMET IMPACT! 💥</h1>
            <p className="text-2xl mb-4 text-orange-300">The grammar comet has struck Earth!</p>
            <div className="text-xl mb-8 bg-red-900 bg-opacity-50 p-6 rounded-lg space-y-2 border-2 border-red-400">
              <p>Final Score: <span className="text-yellow-400 font-bold">{score}</span></p>
              <p>Questions Answered: <span className="text-blue-400 font-bold">{questionsAnswered}</span></p>
              <p>Highest Level: <span className="text-green-400 font-bold">{level}</span></p>
              <p>Best Streak: <span className="text-purple-400 font-bold">{streak}</span></p>
            </div>
            <p className="text-lg mb-6 text-orange-200">
              The grammar mistakes have overwhelmed our defenses! But Earth is resilient - rebuild and try again!
            </p>
            <div className="space-x-4">
              <button 
                onClick={initializeGame}
                className="bg-green-500 hover:bg-green-600 text-white font-bold py-3 px-6 rounded-lg text-xl transform hover:scale-105 transition-all shadow-lg"
              >
                🔄 REBUILD DEFENSES
              </button>
              <button 
                onClick={() => setGameState('menu')}
                className="bg-blue-500 hover:bg-blue-600 text-white font-bold py-3 px-6 rounded-lg text-xl transform hover:scale-105 transition-all shadow-lg"
              >
                🏠 MAIN MENU
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const cometProps = getCometProps();

  // Main game screen
  return (
    <div className="min-h-screen bg-black text-white relative overflow-hidden">
      {/* Animated stars background */}
      <div className="absolute inset-0">
        {[...Array(50)].map((_, i) => (
          <div
            key={i}
            className="absolute w-1 h-1 bg-white rounded-full animate-pulse"
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
              animationDelay: `${Math.random() * 3}s`,
              animationDuration: `${2 + Math.random() * 2}s`
            }}
          />
        ))}
      </div>

      {/* HUD */}
      <div className="absolute top-0 left-0 right-0 z-20 bg-black bg-opacity-70 p-4">
        <div className="flex justify-between items-center max-w-6xl mx-auto text-lg">
          <div>Score: <span className="text-yellow-400 font-bold">{score}</span></div>
          <div>Level: <span className="text-blue-400 font-bold">{level}</span></div>
          <div className={`font-bold ${
            threatLevel === 'critical' ? 'text-red-400 animate-pulse' :
            threatLevel === 'danger' ? 'text-orange-400' :
            threatLevel === 'warning' ? 'text-yellow-400' :
            'text-green-400'
          }`}>
            Threat: {threatLevel.toUpperCase()}
          </div>
          <div>Streak: <span className="text-green-400 font-bold">{streak}</span></div>
          {gameState === 'playing' && (
            <div className="flex items-center space-x-2">
              <span>Time:</span>
              <div className={`font-bold text-xl ${timeLeft <= 5 ? 'text-red-400 animate-pulse' : 'text-white'}`}>
                {timeLeft}s
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Comet distance indicator - moved to bottom left */}
      <div className="absolute bottom-20 left-4 z-20">
        <div className="bg-black bg-opacity-70 p-3 rounded-lg max-w-xs">
          <div className="flex justify-between text-sm mb-2">
            <span>Distance</span>
            <span className="font-bold">{Math.round(cometDistance)}%</span>
          </div>
          <div className="w-full bg-gray-700 rounded-full h-4">
            <div 
              className={`h-4 rounded-full transition-all duration-500 ${
                cometDistance > 75 ? 'bg-green-500' :
                cometDistance > 50 ? 'bg-yellow-500' :
                cometDistance > 25 ? 'bg-orange-500' :
                'bg-red-500 animate-pulse'
              }`}
              style={{ width: `${cometDistance}%` }}
            />
          </div>
        </div>
      </div>

      {/* Power-up indicators */}
      <div className="absolute top-36 left-1/2 transform -translate-x-1/2 z-20 text-center">
        {powerUps.forceField && (
          <div className="text-2xl animate-bounce mb-2 bg-blue-600 px-4 py-2 rounded-lg">
            🛡️ FORCE FIELD ACTIVE!
          </div>
        )}
        {powerUps.gravityBoost && (
          <div className="text-2xl animate-pulse bg-purple-600 px-4 py-2 rounded-lg">
            🌌 GRAVITY BOOST ACTIVE!
          </div>
        )}
      </div>

      {/* Main content area */}
      <div className="pt-44 pb-8 px-4 h-screen relative">
        
        {/* Comet visualization - upper left, moving diagonally toward bottom right where Earth is */}
        <div 
          className="absolute z-10 transition-all duration-1000"
          style={{
            left: `${5 + (100 - cometDistance) * 0.7}%`, // Moves toward bottom right
            top: `${5 + (100 - cometDistance) * 0.8}%`, // Moves down toward Earth
            transform: 'rotate(135deg)' // Angled toward Earth
          }}
        >
          <div 
            className={`transition-all duration-1000 ${cometProps.glow} ${
              threatLevel === 'critical' ? 'animate-bounce' : ''
            }`}
            style={{
              fontSize: `${Math.max(60, 60 + (100 - cometDistance) * 2)}px`,
              opacity: cometProps.opacity,
              filter: `brightness(${0.5 + (100 - cometDistance) / 200})`
            }}
          >
            ☄️
          </div>
          
          {/* Comet trail effect */}
          <div className="absolute top-0 left-0 transform -translate-x-16 -translate-y-16">
            {[...Array(8)].map((_, i) => (
              <div
                key={i}
                className="absolute text-orange-400"
                style={{
                  fontSize: `${Math.max(8, (60 + (100 - cometDistance) * 1.5) * 0.3 - i * 4)}px`,
                  transform: `translate(-${i * 25}px, -${i * 25}px)`,
                  opacity: Math.max(0.1, 0.9 - i * 0.12),
                  filter: `blur(${i * 0.8}px)`
                }}
              >
                ✨
              </div>
            ))}
          </div>
        </div>

        {/* Earth visualization - bottom right corner, positioned well away from questions */}
        <div className="absolute -bottom-32 -right-40 z-10">
          <div className={`transition-all duration-500 ${
            threatLevel === 'critical' ? 'animate-pulse text-red-200' :
            threatLevel === 'danger' ? 'animate-pulse' : ''
          }`}
          style={{ fontSize: '18rem' }} // Slightly smaller and positioned further right
          >🌍</div>
          
          {/* Earth's protective atmosphere glow when shields are active */}
          {(powerUps.forceField || powerUps.gravityBoost) && (
            <div className="absolute inset-0 text-blue-400 animate-pulse opacity-50"
            style={{ fontSize: '18rem' }}>
              🛡️
            </div>
          )}
        </div>

        {/* Question area - upper right, now with proper clearance from Earth */}
        {currentQuestion && gameState === 'playing' && (
          <div className="absolute top-20 right-8 w-80 z-15">
            <div className="bg-blue-900 bg-opacity-90 rounded-lg p-6 shadow-2xl border-4 border-blue-400">
              <div className="text-center mb-4">
                <div className="text-yellow-300 font-bold text-sm uppercase tracking-wide mb-2">
                  ⚡ GRAMMAR DEFLECTOR CHARGING ⚡
                </div>
                <h3 className="text-lg font-bold text-white mb-4">
                  {currentQuestion.question}
                </h3>
              </div>
              
              {/* Answer options */}
              <div className="space-y-3">
                {currentQuestion.options.map((option, index) => (
                  <button
                    key={index}
                    onClick={() => selectAnswer(index)}
                    className="w-full text-left p-3 bg-green-600 hover:bg-green-500 rounded-lg text-sm transition-all transform hover:scale-105 font-medium shadow-lg border-2 border-green-400"
                  >
                    <span className="text-yellow-300 font-bold mr-2">🛡️</span>
                    {option}
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Waiting/Feedback state - center screen */}
        {(gameState === 'waiting' || gameState === 'feedback') && feedback && (
          <div className="absolute inset-0 flex items-center justify-center z-25">
            <div className={`text-3xl font-bold p-8 rounded-lg max-w-2xl text-center ${
              feedbackType === 'correct' ? 'bg-green-600' :
              feedbackType === 'incorrect' ? 'bg-red-600' :
              feedbackType === 'timeout' ? 'bg-orange-600' :
              feedbackType === 'levelup' ? 'bg-purple-600' :
              'bg-blue-600'
            }`}>
              {feedback}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default GrammarDefender;